import { DeckboardIconButtonProps } from "@common/components/ListDetailViews/common/Deckboard/DeckboardIconButton";
import Grid from "@mui/material/Grid2";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import {
  AttachMoneyRounded,
  Call,
  Info,
  StickyNote2Rounded,
  UpdateRounded,
  Repeat,
} from "@mui/icons-material";
import LeftPanel from "@common/components/ListDetailViews/common/LeftPanel";
import AssetCharacteristics, {
  RoomTypeInterface,
} from "@common/components/ListDetailViews/ListView/AssetCharacteristics";
import { SummaryRoomProps } from "@common/components/ListDetailViews/common/SummaryRoom";
import React, { PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import { ElogRounded } from "@assets/icons";
import DeckboardDrawer, {
  DeckboardDrawerProps,
} from "@common/components/ListDetailViews/common/Deckboard/DeckboardDrawer";
import Accordions from "./Accordions";
import { canViewElogsAndRevisions } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { isSameDay } from "date-fns";
import {
  convertLocalTime,
  displayDate,
  displayTime,
} from "@common/utils/localTime.utils";
import { useNavigate } from "react-router-dom";
import {
  ConferenceStatus,
  MeetingType,
  MeetingTypeLabels,
  ProducerService,
  ScheduledMeeting,
} from "@services/meetings/types";
import { updateMeeting } from "@services/meetings";
import { putMeetingSchedule } from "@services/meetings";
import { ApiError } from "@common/api";
import { useErrorNotification } from "@common/components/Notification";
import {
  isMeetingInProgress,
  isMeetingFinished,
  isRoomSweepMeeting,
} from "@common/utils/meetings";
import { HeaderSummaryRoomProps } from "@common/components/ListDetailViews/common/HeaderSummaryRoom";

type ItemPanelProps = {
  setAccordionContent: (value?: React.JSX.Element) => void;
};

enum DeckboardState {
  DIALIN,
  REVISIONS,
  COMMENTS,
  INFO,
  BILLING,
  ELOG,
  RECURRENCE,
}

const ItemPanel = (props: React.PropsWithChildren<ItemPanelProps>) => {
  const { setAccordionContent = () => {} } = props;
  const [state, setState] = useState<DeckboardState | undefined>();
  const { t } = useTranslation();
  const context = React.useContext(RoomsRealtimeContext);
  const meeting = { ...context.detail?.meeting };
  const [user] = useAuthenticatedUser();
  const navigate = useNavigate();
  const dispatchError = useErrorNotification();

  const labels: { [k in DeckboardState]: string } = {
    [DeckboardState.DIALIN]: t("Dial-in information"),
    [DeckboardState.REVISIONS]: t("Revisions"),
    [DeckboardState.COMMENTS]: t("Meeting Notes"),
    [DeckboardState.INFO]: t("Meeting information"),
    [DeckboardState.BILLING]: t("Billing"),
    [DeckboardState.ELOG]: t("E-Logs"),
    [DeckboardState.RECURRENCE]: t("Frequency"),
  };

  const getSelectMeetingStatus = (): HeaderSummaryRoomProps["select"] => {
    if (meeting.status === ConferenceStatus.Cancelled) {
      return {
        tooltip: t("The status of a cancelled meeting cannot be changed"),
        color: "grey",
        items: [{ label: t("Cancelled"), value: ConferenceStatus.Cancelled }],
        value: ConferenceStatus.Cancelled,
        onClick: () => {},
        disabled: true,
      };
    }
    return {
      color: meeting.status === ConferenceStatus.Failed ? "error" : "success",
      items: [
        { label: t("Scheduled"), value: ConferenceStatus.Scheduled },
        { label: t("In Progress"), value: ConferenceStatus.InProgress },
        { label: t("Completed"), value: ConferenceStatus.Completed },
        { label: t("Failed"), value: ConferenceStatus.Failed },
      ],
      value:
        meeting.status === ConferenceStatus.InProgressPartial
          ? ConferenceStatus.InProgress
          : (meeting.status as ConferenceStatus),
      onClick: async (value: string) => {
        try {
          await updateMeeting(meeting.id as number, {
            ...meeting,
            status: value as ConferenceStatus,
          });
          context.detail?.refetch();
        } catch (e) {
          dispatchError(e);
        }
      },
    };
  };
  const meetingInformation: SummaryRoomProps = {
    number: meeting.meetingId,
    select: getSelectMeetingStatus(),
    name: meeting.title ?? "",
    previous: String(meeting?.meetingId),
    type: MeetingTypeLabels[meeting.type as MeetingType] ?? "",
    notificationsNumber: meeting.numOpenTickets,
    hostMCU: meeting?.scheduledMeeting?.hostMcu
      ? `${meeting?.scheduledMeeting?.hostMcu?.device?.room?.name} (${meeting?.scheduledMeeting?.hostMcu?.device?.ipAddress})`
      : undefined,
    onClickHostMCU: () =>
      navigate(
        `/assets/devices/${meeting.scheduledMeeting?.hostMcu?.device?.id}`,
        { state: { previous: t("MEETING") } }
      ),
    account: {
      name: meeting.account?.name ?? "",
      id: meeting.account?.id ?? "",
    },
    dates: {
      day: isSameDay(
        convertLocalTime(new Date(meeting.start as number)),
        new Date()
      )
        ? t("Today")
        : displayDate(meeting.start),
      setupTime: displayTime(meeting.setupTime ?? 0),
      startTime: displayTime(meeting.start),
      endTime: displayTime(meeting.end),
    },
  };

  const handleStateChange = (newState?: DeckboardState) => {
    const onClose = () => handleStateChange(newState);
    setState((state) => {
      if (state === newState || newState === undefined) {
        setAccordionContent(undefined);
        return undefined;
      }
      let children: PropsWithChildren<DeckboardDrawerProps>["children"];
      let title = labels[newState];

      switch (newState) {
        case DeckboardState.DIALIN:
          children = <Accordions.Dialin />;
          break;
        case DeckboardState.REVISIONS:
          children = <Accordions.Revisions />;
          break;
        case DeckboardState.COMMENTS:
          children = <Accordions.Comments />;
          break;
        case DeckboardState.INFO:
          children = <Accordions.Info />;
          break;
        case DeckboardState.BILLING:
          children = <Accordions.Billing />;
          break;
        case DeckboardState.ELOG:
          children = <Accordions.Elog />;
          break;
        case DeckboardState.RECURRENCE:
          children = <Accordions.Recurrence />;
          break;
      }
      setAccordionContent(
        <DeckboardDrawer {...{ title, onClose, children }} />
      );
      return newState;
    });
  };

  const handleCharacteristicsChange = async (value: boolean, key: string) => {
    try {
      let temp: ScheduledMeeting = Object.assign({}, meeting.scheduledMeeting, {
        [key]: value,
      });
      await putMeetingSchedule(meeting.meetingId, temp);
    } catch (e) {
      dispatchError(e);
    }
    await context.detail?.refetch();
  };

  const getBasicProps = (
    state: DeckboardState | undefined,
    buttonState: DeckboardState
  ): Pick<DeckboardIconButtonProps, "label" | "state" | "onClick"> => ({
    label: labels[buttonState],
    state: state === buttonState,
    onClick: () => handleStateChange(buttonState),
  });

  const getDeckboardButtons = () => {
    const deckboardButtons = [
      {
        ...getBasicProps(state, DeckboardState.DIALIN),
        icon: {
          element: <Call />,
        },
        disabled:
          context.detail?.meeting?.type === MeetingType.AdHoc ||
          context.detail?.meeting?.id == undefined,
      },
      {
        ...getBasicProps(state, DeckboardState.REVISIONS),
        icon: {
          element: <UpdateRounded />,
        },
        disabled:
          !canViewElogsAndRevisions(user) ||
          context.detail?.meeting?.id == undefined,
      },
      {
        ...getBasicProps(state, DeckboardState.COMMENTS),
        icon: {
          element: <StickyNote2Rounded />,
        },
      },
      {
        ...getBasicProps(state, DeckboardState.INFO),
        icon: {
          element: <Info />,
        },
      },
      {
        ...getBasicProps(state, DeckboardState.BILLING),
        icon: {
          element: <AttachMoneyRounded />,
        },
        disabled:
          !canViewElogsAndRevisions(user) ||
          context.detail?.meeting?.id == undefined ||
          isRoomSweepMeeting(context.detail?.meeting),
      },
      {
        ...getBasicProps(state, DeckboardState.ELOG),
        icon: {
          element: <ElogRounded />,
        },
        disabled:
          !canViewElogsAndRevisions(user) ||
          context.detail?.meeting?.id == undefined ||
          isRoomSweepMeeting(context.detail?.meeting),
      },
    ];
    if (meeting.scheduledMeeting?.parentMeeting)
      deckboardButtons.push({
        ...getBasicProps(state, DeckboardState.RECURRENCE),
        icon: {
          element: <Repeat />,
        },
      });

    return deckboardButtons;
  };
  const producerRequested =
    (meeting?.scheduledMeeting?.producerService &&
      meeting?.scheduledMeeting?.producerService !==
        ProducerService.NoAttendance) ||
    false;

  const meetingTypes: RoomTypeInterface[] = [
    {
      label: "VIP",
      status: meeting?.vip ?? false,
      key: "vip",
      disabled: isMeetingInProgress(meeting) || isMeetingFinished(meeting),
      handleChange: handleCharacteristicsChange,
    },
    {
      label: "Recording Requested",
      status: meeting?.scheduledMeeting?.recorded ?? false,
      key: "recorded",
      disabled:
        meeting.scheduledMeeting === undefined ||
        isMeetingInProgress(meeting) ||
        isMeetingFinished(meeting),
      handleChange: handleCharacteristicsChange,
    },
    {
      label: "Producer Requested",
      status: producerRequested,
      key: undefined,
      disabled:
        meeting.scheduledMeeting === undefined ||
        isMeetingInProgress(meeting) ||
        isMeetingFinished(meeting),
    },
  ];

  return (
    <Grid
      sx={(theme) => ({
        padding: theme.spacing(0, 4),
        maxWidth: theme.spacing(50),
        minWidth: theme.spacing(50),
        height: "100%",
      })}
    >
      <Grid
        container
        direction="column"
        sx={{ width: "100%", alignItems: "center" }}
      >
        <LeftPanel
          roomInformation={meetingInformation}
          deckboardButtons={getDeckboardButtons()}
        >
          <AssetCharacteristics roomTypes={meetingTypes} />
        </LeftPanel>
      </Grid>
    </Grid>
  );
};

export default ItemPanel;
